import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Row,
  Select, Space, Table, Tabs, Tag, Typography, message,
} from 'antd';
import dayjs from 'dayjs';
import { PlusOutlined } from '@ant-design/icons';
import {
  Equipment, getEquipment, createEquipment, updateEquipment,
} from '../api/equipment';
import api from '../api/client';
import MaintenanceLog from '../components/MaintenanceLog';

const { Title } = Typography;

interface Department { id: number; name: string; }
interface Employee { id: number; first_name: string; last_name: string; }

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  repair: 'orange',
  stored: 'blue',
  written_off: 'default',
};
const STATUS_LABELS: Record<string, string> = {
  active: 'В работе',
  repair: 'В ремонте',
  stored: 'На хранении',
  written_off: 'Списан',
};

const PURCHASE_SOURCES = ['Озон', 'Все Инструменты', 'Гарвин'];

const ToolsPage: React.FC = () => {
  const [items, setItems] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);

  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [filterDept, setFilterDept] = useState<number | undefined>();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<Equipment | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    api.get<Department[]>('/departments').then(r => {
      const d = r.data as any;
      setDepartments(Array.isArray(d) ? d : d.items ?? []);
    });
    api.get<Employee[]>('/employees').then(r => {
      const d = r.data as any;
      setEmployees(Array.isArray(d) ? d : d.items ?? []);
    });
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEquipment({
        category: 'tool',
        department_id: filterDept,
      });
      setItems(filterStatus ? data.filter(i => i.status === filterStatus) : data);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterDept]);

  useEffect(() => { loadData(); }, [loadData]);

  const openCreate = async () => {
    setSelected(null);
    form.resetFields();
    const today = dayjs();
    form.setFieldsValue({
      status: 'active',
      year: new Date().getFullYear(),
      purchase_date: today,
      issue_date: today,
    });
    setDrawerOpen(true);
    try {
      const r = await api.get<{ next_number: string }>('/equipment/next-number', { params: { category: 'tool' } });
      form.setFieldsValue({ inventory_number: r.data.next_number });
    } catch { /* пользователь введёт вручную */ }
  };

  const openEdit = (record: Equipment) => {
    setSelected(record);
    form.setFieldsValue({
      inventory_number: record.inventory_number,
      name: record.name,
      brand: record.brand,
      model: record.model,
      year: record.year,
      purchase_cost: record.purchase_cost,
      purchase_source: record.purchase_source,
      purchase_date: record.purchase_date ? dayjs(record.purchase_date) : null,
      issue_date: record.issue_date ? dayjs(record.issue_date) : null,
      status: record.status,
      department_id: record.department_id,
      assigned_employee_id: record.assigned_employee_id,
    });
    setDrawerOpen(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        ...values,
        category: 'tool',
        workplace_ids: [],
        purchase_date: values.purchase_date?.format('YYYY-MM-DD') ?? null,
        issue_date: values.issue_date?.format('YYYY-MM-DD') ?? null,
      };
      if (selected) {
        const updated = await updateEquipment(selected.id, payload);
        setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
        setSelected(updated);
        message.success('Обновлено');
      } else {
        const created = await createEquipment(payload);
        setItems(prev => [...prev, created]);
        setDrawerOpen(false);
        message.success('Создано');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка при сохранении');
    }
  };

  const columns = [
    { title: 'Инв. номер', dataIndex: 'inventory_number', key: 'inv', width: 130 },
    { title: 'Наименование', dataIndex: 'name', key: 'name' },
    { title: 'Марка / Модель', key: 'brand',
      render: (_: any, r: Equipment) => [r.brand, r.model].filter(Boolean).join(' / ') || '—' },
    { title: 'Статус', key: 'status', width: 120,
      render: (_: any, r: Equipment) => (
        <Tag color={STATUS_COLORS[r.status ?? 'active']}>{STATUS_LABELS[r.status ?? 'active']}</Tag>
      ),
    },
    { title: 'Подразделение', key: 'dept', width: 160,
      render: (_: any, r: Equipment) => r.department?.name ?? '—' },
    { title: 'Стоимость', key: 'cost', width: 120, align: 'right' as const,
      render: (_: any, r: Equipment) =>
        r.purchase_cost !== null && r.purchase_cost !== undefined
          ? `${Number(r.purchase_cost).toLocaleString('ru-RU')} ₽`
          : '—',
    },
    { title: 'Где куплен', key: 'source', width: 140,
      render: (_: any, r: Equipment) => r.purchase_source ?? '—' },
    { title: 'Дата покупки', key: 'purchase_date', width: 120,
      render: (_: any, r: Equipment) => r.purchase_date ?? '—' },
    { title: 'Дата выдачи', key: 'issue_date', width: 120,
      render: (_: any, r: Equipment) => r.issue_date ?? '—' },
    { title: 'Кому выдан', key: 'emp', width: 160,
      render: (_: any, r: Equipment) =>
        r.assigned_employee
          ? `${(r.assigned_employee as any).last_name} ${(r.assigned_employee as any).first_name[0]}.`
          : '—',
    },
  ];

  const tabItems = selected ? [
    {
      key: 'data',
      label: 'Данные',
      children: (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={12}>
            <Col span={10}>
              <Form.Item name="inventory_number" label="Инвентарный номер" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={14}>
              <Form.Item name="name" label="Наименование" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="brand" label="Марка"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="model" label="Модель"><Input /></Form.Item></Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="status" label="Статус">
                <Select options={Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="year" label="Год выпуска">
                <InputNumber style={{ width: '100%' }} min={1900} max={2100} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="purchase_cost" label="Стоимость (₽)">
                <InputNumber style={{ width: '100%' }} min={0} precision={2} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="purchase_source" label="Где куплен">
                <Select
                  allowClear
                  placeholder="Не указано"
                  showSearch
                  options={PURCHASE_SOURCES.map(s => ({ value: s, label: s }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="purchase_date" label="Дата покупки">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" placeholder="дд.мм.гггг" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="issue_date" label="Дата выдачи">
                <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" placeholder="дд.мм.гггг" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="department_id" label="Подразделение">
            <Select allowClear placeholder="Не указано"
              options={departments.map(d => ({ value: d.id, label: d.name }))} />
          </Form.Item>
          <Form.Item name="assigned_employee_id" label="Кому выдан">
            <Select allowClear placeholder="Не выдан"
              options={employees.map(e => ({ value: e.id, label: `${e.last_name} ${e.first_name}` }))} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Сохранить</Button>
          </Form.Item>
        </Form>
      ),
    },
    {
      key: 'maintenance',
      label: 'Журнал обслуживания',
      children: <MaintenanceLog equipmentId={selected.id} />,
    },
  ] : [];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Title level={4} style={{ margin: 0 }}>Инструменты</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Добавить</Button>
      </Space>

      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="180px">
          <Select allowClear placeholder="Статус" style={{ width: '100%' }}
            options={Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))}
            onChange={setFilterStatus} />
        </Col>
        <Col flex="200px">
          <Select allowClear placeholder="Подразделение" style={{ width: '100%' }}
            options={departments.map(d => ({ value: d.id, label: d.name }))}
            onChange={setFilterDept} />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={items}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20 }}
        onRow={record => ({ onClick: () => openEdit(record), style: { cursor: 'pointer' } })}
      />

      {/* Drawer создания (без вкладок) */}
      {!selected && (
        <Drawer title="Новый инструмент" open={drawerOpen} onClose={() => setDrawerOpen(false)} width={480}>
          <Form form={form} layout="vertical" onFinish={handleSubmit}>
            <Form.Item name="inventory_number" label="Инвентарный номер" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="name" label="Наименование" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}><Form.Item name="brand" label="Марка"><Input /></Form.Item></Col>
              <Col span={12}><Form.Item name="model" label="Модель"><Input /></Form.Item></Col>
            </Row>
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="status" label="Статус" initialValue="active">
                  <Select options={Object.entries(STATUS_LABELS).map(([v, l]) => ({ value: v, label: l }))} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="year" label="Год выпуска">
                  <InputNumber style={{ width: '100%' }} min={1900} max={2100} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="purchase_cost" label="Стоимость (₽)"
                  rules={[{ type: 'number', min: 0, message: 'Стоимость не может быть отрицательной' }]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={8}>
                <Form.Item name="purchase_source" label="Где куплен">
                  <Select
                    allowClear
                    placeholder="Не указано"
                    showSearch
                    options={PURCHASE_SOURCES.map(s => ({ value: s, label: s }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="purchase_date" label="Дата покупки">
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" placeholder="дд.мм.гггг" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="issue_date" label="Дата выдачи">
                  <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" placeholder="дд.мм.гггг" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="department_id" label="Подразделение">
              <Select allowClear placeholder="Не указано"
                options={departments.map(d => ({ value: d.id, label: d.name }))} />
            </Form.Item>
            <Form.Item name="assigned_employee_id" label="Кому выдан">
              <Select allowClear placeholder="Не выдан"
                options={employees.map(e => ({ value: e.id, label: `${e.last_name} ${e.first_name}` }))} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>Создать</Button>
            </Form.Item>
          </Form>
        </Drawer>
      )}

      {/* Drawer редактирования с вкладками */}
      {selected && (
        <Drawer
          title={`${selected.name} (${selected.inventory_number})`}
          open={drawerOpen}
          onClose={() => { setDrawerOpen(false); setSelected(null); }}
          width={600}
        >
          <Tabs items={tabItems} />
        </Drawer>
      )}
    </div>
  );
};

export default ToolsPage;
